import { useState, useEffect, useCallback } from "react";

interface UseFullscreenReturn {
  isFullscreen: boolean;
  enter: () => Promise<void>;
  exit: () => Promise<void>;
  toggle: () => Promise<void>;
  /** False when the Fullscreen API is not available (e.g. SSR, insecure context). */
  supported: boolean;
}

/**
 * Wraps the browser Fullscreen API on `document.documentElement`.
 *
 * SSR-safe: all API access is guarded by `typeof document !== "undefined"`.
 * Degrades gracefully when the API is unavailable (`supported: false`).
 * Listens to `fullscreenchange` to keep `isFullscreen` in sync even when the
 * user exits via the Escape key.
 *
 * Never throws — errors from enter/exit are silently swallowed (the browser
 * may reject the request if there is no user gesture, for example).
 */
export function useFullscreen(): UseFullscreenReturn {
  const supported =
    typeof document !== "undefined" &&
    typeof document.documentElement.requestFullscreen === "function";

  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    if (!supported) return;

    function onChange() {
      setIsFullscreen(!!document.fullscreenElement);
    }

    document.addEventListener("fullscreenchange", onChange);
    // Sync current state in case we mounted into an already-fullscreen document.
    setIsFullscreen(!!document.fullscreenElement);

    return () => {
      document.removeEventListener("fullscreenchange", onChange);
    };
  }, [supported]);

  const enter = useCallback(async () => {
    if (!supported) return;
    try {
      await document.documentElement.requestFullscreen();
    } catch {
      // No user gesture, or API unavailable — ignore.
    }
  }, [supported]);

  const exit = useCallback(async () => {
    if (!supported) return;
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      }
    } catch {
      // Ignore.
    }
  }, [supported]);

  const toggle = useCallback(async () => {
    if (document.fullscreenElement) {
      await exit();
    } else {
      await enter();
    }
  }, [enter, exit]);

  return { isFullscreen, enter, exit, toggle, supported };
}
